import type WebNotificationPreferencesPage from '@/shared/types/web/WebNotificationPreferencesPage'

export interface GuidedFormState {
  selectedSiteIds: number[]
  allSites: boolean
  selectedDepartmentIds: number[]
  allDepartments: boolean
  selectedEventTypeIds: number[]
  selectedFrequencies: ('immediate' | 'daily' | 'weekly')[]
  deliverByEmail: boolean
  deliverBySms: boolean
}

interface ExpandedPayload {
  notification_frequency: { immediate: boolean; daily: boolean; weekly: boolean }
  frequency_bands: {
    immediate: { code_value_id: number | null; notification_type_ids: number[] }
    daily: { code_value_id: number | null; notification_type_ids: number[] }
    weekly: { code_value_id: number | null; notification_type_ids: number[] }
  }
  preference_rows: {
    id: number | null
    notification_type_id: number
    site_id: number | null
    department_id: number | null
    deliver_by_email: boolean
    deliver_by_sms: boolean
  }[]
  department_subscription_ids: number[]
}

export const DEFAULT_FORM_STATE: GuidedFormState = {
  selectedSiteIds: [],
  allSites: true,
  selectedDepartmentIds: [],
  allDepartments: true,
  selectedEventTypeIds: [],
  selectedFrequencies: ['immediate'],
  deliverByEmail: true,
  deliverBySms: false,
}

// Guardrail against the (event types × sites × departments) cartesian producing an
// unbounded preference_rows payload. Chosen well above any realistic single-user
// configuration but low enough to keep one PATCH transaction bounded.
export const MAX_PREFERENCE_ROWS = 500

/**
 * Count of preference_rows the current selection would expand to, without building them.
 */
export const projectedPreferenceRowCount = (formState: GuidedFormState): number => {
  // REBUILD-513: rows are no longer a site × department cartesian. When specific
  // departments are selected, each department is one (own-site, department) pair;
  // otherwise there is one pair per selected site (or a single account-wide pair).
  const scopePairCount = formState.allDepartments
    ? formState.allSites
      ? 1
      : Math.max(formState.selectedSiteIds.length, 0)
    : Math.max(formState.selectedDepartmentIds.length, 0)
  return formState.selectedEventTypeIds.length * scopePairCount
}

/**
 * Detects whether the user has heterogeneous delivery (different email/sms flags
 * across their saved preference rows). Used to show the mixed-delivery note.
 */
export const detectMixedDelivery = (page: WebNotificationPreferencesPage): boolean => {
  const rows = page.preferenceRows
  if (rows.length < 2) return false
  const first = rows[0]
  return rows.some(
    (row) => row.deliverByEmail !== first.deliverByEmail || row.deliverBySms !== first.deliverBySms,
  )
}

/**
 * Detects whether the saved rows mix an account-wide (null) scope with a specific
 * site/department scope. The guided form collapses this to the specific scope on
 * hydration (see extractScopeFromRows); the flag lets the UI warn that saving will
 * drop the account-wide fallback rows.
 */
export const detectMixedScope = (page: WebNotificationPreferencesPage): boolean => {
  const rows = page.preferenceRows
  if (rows.length === 0) return false
  const hasNullSite = rows.some((row) => row.siteId == null)
  const hasSpecificSite = rows.some((row) => row.siteId != null)
  const hasNullDepartment = rows.some((row) => row.departmentId == null)
  const hasSpecificDepartment = rows.some((row) => row.departmentId != null)
  return (hasNullSite && hasSpecificSite) || (hasNullDepartment && hasSpecificDepartment)
}

/**
 * Hydrate guided form state from the flat page data (loaded from server).
 * Converts the three parallel record sets into a single guided form state.
 */
export const hydrateFormFromPage = (page: WebNotificationPreferencesPage): GuidedFormState => {
  const hasNoConfig = hasNoSavedConfiguration(page)
  if (hasNoConfig) {
    return {
      ...DEFAULT_FORM_STATE,
      selectedEventTypeIds: page.notificationTypes.map((type) => type.id),
    }
  }

  const frequencies: ('immediate' | 'daily' | 'weekly')[] = []
  if (page.notificationFrequency.immediate) frequencies.push('immediate')
  if (page.notificationFrequency.daily) frequencies.push('daily')
  if (page.notificationFrequency.weekly) frequencies.push('weekly')

  const eventTypeIds = extractEventTypeIds(page)

  const { siteIds, departmentIds, allSites, allDepartments } = extractScopeFromRows(page)

  const { deliverByEmail, deliverBySms } = extractChannels(page)

  // The user's saved frequency bits are the source of truth. Never default a
  // non-empty config back to Immediate — that would silently re-enable a band the
  // user turned off. Only fall back when the config genuinely carries no frequency.
  return {
    selectedSiteIds: siteIds,
    allSites,
    selectedDepartmentIds: departmentIds,
    allDepartments,
    selectedEventTypeIds: eventTypeIds,
    selectedFrequencies: frequencies,
    deliverByEmail,
    deliverBySms,
  }
}

/**
 * Expand guided form state into the S30 payload shape expected by
 * PreferencePageSaveService. The cartesian product of (event types × scope) produces
 * preference_rows; department selections drive department_subscription_ids.
 */
export const expandFormToPayload = (
  formState: GuidedFormState,
  page: WebNotificationPreferencesPage,
): ExpandedPayload => {
  const frequencyMap = {
    immediate: formState.selectedFrequencies.includes('immediate'),
    daily: formState.selectedFrequencies.includes('daily'),
    weekly: formState.selectedFrequencies.includes('weekly'),
  }

  const bandForKey = (band: 'immediate' | 'daily' | 'weekly') => ({
    code_value_id: page.frequencyBands[band].codeValueId,
    notification_type_ids: frequencyMap[band] ? formState.selectedEventTypeIds : [],
  })

  const preferenceRows = buildPreferenceRows(formState, page)

  const departmentSubscriptionIds = resolveDepartmentSubscriptionIds(formState, page)

  return {
    notification_frequency: frequencyMap,
    frequency_bands: {
      immediate: bandForKey('immediate'),
      daily: bandForKey('daily'),
      weekly: bandForKey('weekly'),
    },
    preference_rows: preferenceRows,
    department_subscription_ids: departmentSubscriptionIds,
  }
}

function hasNoSavedConfiguration(page: WebNotificationPreferencesPage): boolean {
  const noCategories =
    page.frequencyBands.immediate.notificationTypeIds.length === 0 &&
    page.frequencyBands.daily.notificationTypeIds.length === 0 &&
    page.frequencyBands.weekly.notificationTypeIds.length === 0
  return noCategories && page.preferenceRows.length === 0
}

/**
 * Resolve the concrete department ids to persist as department subscriptions
 * (the `Notification` table). Recipient resolution (DispatchEventService) requires
 * an explicit Notification row per department, so "All Departments" must be
 * materialized into every accessible department id — never an empty list, which
 * the backend would interpret as "no subscriptions" (legacy parity: the save proc
 * inserts one notifications row per real department id).
 *
 * When specific sites are selected, the accessible departments are constrained to
 * those sites, mirroring the filteredDepartments logic on the preferences page.
 */
function resolveDepartmentSubscriptionIds(
  formState: GuidedFormState,
  page: WebNotificationPreferencesPage,
): number[] {
  if (!formState.allDepartments) return formState.selectedDepartmentIds

  if (formState.allSites || formState.selectedSiteIds.length === 0) {
    return page.departments.map((department) => department.id)
  }

  const allowedSiteIds = new Set(formState.selectedSiteIds)
  return page.departments
    .filter((department) => department.siteId == null || allowedSiteIds.has(department.siteId))
    .map((department) => department.id)
}

function extractEventTypeIds(page: WebNotificationPreferencesPage): number[] {
  const allIds = new Set<number>()
  for (const typeId of page.frequencyBands.immediate.notificationTypeIds) allIds.add(typeId)
  for (const typeId of page.frequencyBands.daily.notificationTypeIds) allIds.add(typeId)
  for (const typeId of page.frequencyBands.weekly.notificationTypeIds) allIds.add(typeId)
  return Array.from(allIds)
}

function extractScopeFromRows(page: WebNotificationPreferencesPage) {
  const siteIdsFromRows = new Set<number>()
  const departmentIdsFromRows = new Set<number>()
  let hasNullSite = false
  let hasNullDepartment = false

  for (const row of page.preferenceRows) {
    if (row.siteId != null) siteIdsFromRows.add(row.siteId)
    else hasNullSite = true
    if (row.departmentId != null) departmentIdsFromRows.add(row.departmentId)
    else hasNullDepartment = true
  }

  // A specific site/department scope wins over the account-wide (null) sentinel.
  // Legacy configs can mix a null row with specific rows; treating that as
  // "all sites/departments" would silently drop the specific scope on the next save.
  // Prefer the concrete ids and surface the ambiguity via detectMixedScope instead.
  const allSites = siteIdsFromRows.size === 0 && (hasNullSite || page.preferenceRows.length > 0)

  const specificDepartmentIds =
    page.departmentSubscriptionIds.length > 0
      ? new Set(page.departmentSubscriptionIds)
      : departmentIdsFromRows
  const allDepartments =
    specificDepartmentIds.size === 0 && (hasNullDepartment || page.preferenceRows.length > 0)

  const siteIds = allSites ? [] : Array.from(siteIdsFromRows)
  const departmentIds = allDepartments ? [] : Array.from(specificDepartmentIds)

  return { siteIds, departmentIds, allSites, allDepartments }
}

function extractChannels(page: WebNotificationPreferencesPage) {
  if (page.preferenceRows.length === 0) {
    return { deliverByEmail: true, deliverBySms: false }
  }
  const hasEmail = page.preferenceRows.some((row) => row.deliverByEmail)
  const hasSms = page.preferenceRows.some((row) => row.deliverBySms)
  return { deliverByEmail: hasEmail || !hasSms, deliverBySms: hasSms }
}

/**
 * Build preference_rows from the form state WITHOUT crossing sites and departments.
 *
 * REBUILD-513: the previous implementation produced a (site × department) cartesian
 * product, so a department belonging to site A was also emitted on a row for site B.
 * `department_must_match_site_when_both_present` then rejected those crossed pairs
 * (translation-missing banner), and the id-reuse composite could collide (duplicate-row
 * banner). Instead, pair each selected department with ITS OWN site (resolved from
 * page.departments), and only use the selected sites directly when departments resolve
 * to "All departments" for a site.
 *
 * Scope rules:
 *  - All sites + All departments  → one account-wide row (site_id=null, department_id=null).
 *  - Specific sites + All departments → one row per selected site (site_id set, department_id=null).
 *  - All sites + specific departments → one row per department paired with its own site.
 *  - Specific sites + specific departments → one row per department paired with its own site.
 *
 * Reuses existing row IDs where the (notification_type_id, site_id, department_id) triple matches.
 */
function buildPreferenceRows(formState: GuidedFormState, page: WebNotificationPreferencesPage) {
  const existingByKey = new Map<string, number>()
  for (const row of page.preferenceRows) {
    const key = `${row.notificationTypeId}|${row.siteId ?? ''}|${row.departmentId ?? ''}`
    existingByKey.set(key, row.id!)
  }

  const siteByDepartmentId = new Map<number, number | null>()
  for (const department of page.departments) {
    siteByDepartmentId.set(department.id, department.siteId ?? null)
  }

  // Each entry is a non-crossed (site_id, department_id) scope pair for the current selection.
  const scopePairs: { siteId: number | null; departmentId: number | null }[] = []

  if (formState.allDepartments) {
    // No specific departments: one row per selected site (or a single account-wide row).
    const siteScope: (number | null)[] = formState.allSites ? [null] : formState.selectedSiteIds
    for (const siteId of siteScope) {
      scopePairs.push({ siteId, departmentId: null })
    }
  } else {
    // Specific departments: pair each with its OWN site, never a crossed site.
    for (const departmentId of formState.selectedDepartmentIds) {
      const siteId = siteByDepartmentId.has(departmentId)
        ? (siteByDepartmentId.get(departmentId) ?? null)
        : null
      scopePairs.push({ siteId, departmentId })
    }
  }

  const rows: ExpandedPayload['preference_rows'] = []

  for (const eventTypeId of formState.selectedEventTypeIds) {
    for (const { siteId, departmentId } of scopePairs) {
      const key = `${eventTypeId}|${siteId ?? ''}|${departmentId ?? ''}`
      const existingId = existingByKey.get(key) ?? null
      rows.push({
        id: existingId,
        notification_type_id: eventTypeId,
        site_id: siteId,
        department_id: departmentId,
        deliver_by_email: formState.deliverByEmail,
        deliver_by_sms: formState.deliverBySms,
      })
    }
  }

  return rows
}
